import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [processingUser, setProcessingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Fetch pending users after users are loaded
    if (users.length > 0) {
      fetchPendingUsers();
    }
  }, [users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      setError('Failed to load users');
      console.error('Error fetching users:', error);
    }
  };

  // Helper function to find creator name by ID
  const getCreatorName = (createdById) => {
    if (!createdById) return null;
    
    // If it's already an object (populated), return the name
    if (typeof createdById === 'object' && createdById.name) {
      return createdById.name;
    }
    
    // If it's just an ID string, look up the user in our users list
    if (typeof createdById === 'string') {
      const creator = users.find(user => user._id === createdById);
      return creator ? creator.name : null;
    }
    
    return null;
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/pending`);
      console.log('Pending users from backend:', response.data); // Debug log
      
      // Since production backend may not have our new logic, let's also check all users for pending ones
      const allPendingUsers = users.filter(user => 
        user.status === 'pending_approval' || user.graderRequestStatus === 'pending'
      );
      console.log('Pending users found in all users:', allPendingUsers); // Debug log
      
      // Use the more comprehensive list if we have it
      const finalPendingUsers = allPendingUsers.length > response.data.length ? allPendingUsers : response.data;
      setPendingUsers(finalPendingUsers);
    } catch (error) {
      console.error('Error fetching pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    setProcessingUser(userId);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${userId}/approve`);
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to approve user');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleRejectUser = async (userId) => {
    setProcessingUser(userId);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${userId}/reject`);
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reject user');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setProcessingUser(userId);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${userId}/role`, { role: newRole });
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to change role');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate "${userName}"'s account? They will need to be re-approved to access the system again.`)) {
      return;
    }

    setProcessingUser(userId);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${userId}/deactivate`);
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to deactivate user');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    setProcessingUser(userId);
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}`);
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name, email: user.email });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setEditForm({ name: '', email: '' });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;

    setProcessingUser(editingUser._id);
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${editingUser._id}/profile`, {
        name: editForm.name.trim(),
        email: editForm.email.trim()
      });
      
      alert(response.data.message);
      await fetchUsers();
      await fetchPendingUsers();
      handleCloseEditModal();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update user');
    } finally {
      setProcessingUser(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return '#dc3545';
      case 'grader': return '#28a745';
      case 'student': return '#007bff';
      default: return '#6c757d';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'pending_approval': return '#ffc107';
      case 'rejected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // Filter function for users
  const filterUsers = (userList) => {
    if (!searchQuery.trim()) {
      return userList;
    }

    const query = searchQuery.toLowerCase();
    return userList.filter(user => 
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query) ||
      (user.studentId && user.studentId.toLowerCase().includes(query))
    );
  };

  const filteredUsers = filterUsers(users);
  const filteredPendingUsers = filterUsers(pendingUsers);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>User Management</h1>
          <Link to="/admin" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #ddd' }}>
          <button
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ borderRadius: '8px 8px 0 0' }}
          >
            All Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={activeTab === 'pending' ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ borderRadius: '8px 8px 0 0' }}
          >
            Pending Approval ({pendingUsers.length})
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Search users by name, email, role, or student ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            />
            <svg 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: '#666'
              }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="21 21l-4.35-4.35"></path>
            </svg>
          </div>
          {searchQuery && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              {activeTab === 'all' 
                ? `Showing ${filteredUsers.length} of ${users.length} users`
                : `Showing ${filteredPendingUsers.length} of ${pendingUsers.length} pending users`
              }
              <button
                onClick={() => setSearchQuery('')}
                style={{ 
                  marginLeft: '12px', 
                  color: '#007bff', 
                  background: 'none', 
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <div className="card">
          <h2>Users Pending Approval</h2>
          {filteredPendingUsers.length === 0 ? (
            <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
              <h3>{searchQuery ? 'No Matching Users' : 'No Pending Approvals'}</h3>
              <p>{searchQuery ? 'Try adjusting your search terms.' : 'All users have been processed.'}</p>
            </div>
          ) : (
            <div className="grid grid-1">
              {filteredPendingUsers.map((user) => {
                // Determine approval type
                const isGraderRequest = user.graderRequestStatus === 'pending';
                const isAccountApproval = user.status === 'pending_approval' && user.creationSource === 'self-registration';
                
                return (
                  <div key={user._id} className="card" style={{ backgroundColor: '#fff3cd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0' }}>{user.name}</h3>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {user.email}
                          {user.studentId && (
                            <span> | Student ID: {user.studentId}</span>
                          )}
                        </div>
                        
                        {/* Approval Type Indicator */}
                        <div style={{ marginTop: '8px' }}>
                          {isAccountApproval && (
                            <span 
                              className="badge" 
                              style={{ 
                                backgroundColor: '#17a2b8', 
                                color: 'white',
                                fontSize: '11px',
                                marginRight: '8px'
                              }}
                            >
                              📝 Account Approval Required
                            </span>
                          )}
                          {isGraderRequest && (
                            <span 
                              className="badge" 
                              style={{ 
                                backgroundColor: '#28a745', 
                                color: 'white',
                                fontSize: '11px'
                              }}
                            >
                              🎓 Grader Role Request
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span 
                          className="badge" 
                          style={{ backgroundColor: getRoleBadgeColor(user.role), color: 'white' }}
                        >
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                        <span 
                          className="badge" 
                          style={{ backgroundColor: getStatusBadgeColor(user.status), color: 'white' }}
                        >
                          {user.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* Account Creation Source */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ 
                        fontSize: '13px', 
                        padding: '8px 12px', 
                        backgroundColor: user.creationSource === 'self-registration' ? '#e7f3ff' : '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                      }}>
                        <strong>Creation Source:</strong> {
                          user.creationSource === 'self-registration' 
                            ? '🙋‍♂️ Self-registered account' 
                            : '👨‍💼 Admin-created account'
                        }
                        {user.createdBy && (
                          <span style={{ marginLeft: '8px', color: '#666' }}>
                            (Created by: {user.createdBy.name || 'Admin'})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Grader Request Reason */}
                    {user.graderRequestReason && (
                      <div style={{ marginBottom: '16px' }}>
                        <strong>Grader Request Reason:</strong>
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '12px', 
                          backgroundColor: 'white', 
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }}>
                          {user.graderRequestReason}
                        </div>
                      </div>
                    )}

                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
                      <div>Registered: {formatDate(user.createdAt)}</div>
                      {user.graderRequestedAt && (
                        <div>Grader Role Requested: {formatDate(user.graderRequestedAt)}</div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => handleApproveUser(user._id)}
                        className="btn btn-success"
                        disabled={processingUser === user._id}
                        style={{ flex: 1 }}
                      >
                        {processingUser === user._id ? 'Processing...' : (
                          isAccountApproval ? 'Approve Account' : 
                          isGraderRequest ? 'Approve as Grader' : 'Approve'
                        )}
                      </button>
                      <button
                        onClick={() => handleRejectUser(user._id)}
                        className="btn"
                        disabled={processingUser === user._id}
                        style={{ 
                          flex: 1, 
                          backgroundColor: '#dc3545', 
                          color: 'white',
                          border: '1px solid #dc3545'
                        }}
                      >
                        {processingUser === user._id ? 'Processing...' : (
                          isAccountApproval ? 'Reject Account' :
                          isGraderRequest ? 'Reject Request' : 'Reject'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* All Users Tab */}
      {activeTab === 'all' && (
        <div className="card">
          <h2>All Users</h2>
          {filteredUsers.length === 0 ? (
            <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
              <h3>{searchQuery ? 'No Matching Users' : 'No Users Found'}</h3>
              <p>{searchQuery ? 'Try adjusting your search terms.' : 'No users have been registered yet.'}</p>
            </div>
          ) : (
            <div className="grid grid-1">
              {filteredUsers.map((user) => (
                <div key={user._id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0' }}>{user.name}</h3>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {user.email}
                        {user.studentId && (
                          <span> | Student ID: {user.studentId}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span 
                        className="badge" 
                        style={{ backgroundColor: getRoleBadgeColor(user.role), color: 'white' }}
                      >
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                      <span 
                        className="badge" 
                        style={{ backgroundColor: getStatusBadgeColor(user.status), color: 'white' }}
                      >
                        {user.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Creation Source Information */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      padding: '8px 12px', 
                      backgroundColor: user.creationSource === 'admin-created' ? '#f8f9fa' : '#e7f3ff',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}>
                      <strong>Account Source:</strong> {
                        user.creationSource === 'admin-created' 
                          ? '👨‍💼 Admin-created account' 
                          : user.creationSource === 'self-registration'
                          ? '🙋‍♂️ Self-registered account'
                          : '❓ Legacy account (before tracking)'
                      }
                      {user.createdBy && (
                        <span style={{ marginLeft: '8px', color: '#666' }}>
                          (Created by: {getCreatorName(user.createdBy) || `Admin ID: ${user.createdBy}`})
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
                    <div>Registered: {formatDate(user.createdAt)}</div>
                    {user.approvedBy && (
                      <div>
                        Approved by: {user.approvedBy.name} on {formatDate(user.approvedAt)}
                      </div>
                    )}
                    {user.deactivatedBy && (
                      <div style={{ color: '#dc3545' }}>
                        Deactivated by: {user.deactivatedBy.name} on {formatDate(user.deactivatedAt)}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Edit Button - Super-admins can edit anyone, regular admins cannot edit super-admins */}
                    {(user.role !== 'super-admin' || (user.role === 'super-admin' && currentUser?.role === 'super-admin')) && (
                      <button
                        onClick={() => handleEditUser(user)}
                        className="btn btn-outline"
                        disabled={processingUser === user._id}
                        style={{ fontSize: '12px' }}
                      >
                        Edit Profile
                      </button>
                    )}
                    
                    {/* Role Change Buttons */}
                    {user.role !== 'student' && (
                      <button
                        onClick={() => handleRoleChange(user._id, 'student')}
                        className="btn btn-outline"
                        disabled={processingUser === user._id}
                        style={{ fontSize: '12px' }}
                      >
                        Make Student
                      </button>
                    )}
                    {user.role !== 'grader' && (
                      <button
                        onClick={() => handleRoleChange(user._id, 'grader')}
                        className="btn btn-outline"
                        disabled={processingUser === user._id}
                        style={{ fontSize: '12px' }}
                      >
                        Make Grader
                      </button>
                    )}
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleRoleChange(user._id, 'admin')}
                        className="btn btn-outline"
                        disabled={processingUser === user._id}
                        style={{ fontSize: '12px' }}
                      >
                        Make Admin
                      </button>
                    )}
                    
                    {/* Deactivate Button - Only show for active users, not for current user, and with super-admin protection */}
                    {user.status === 'active' && 
                     user._id !== currentUser?._id && 
                     (user.role !== 'super-admin' || currentUser?.role === 'super-admin') && (
                      <button
                        onClick={() => handleDeactivateUser(user._id, user.name)}
                        className="btn"
                        disabled={processingUser === user._id}
                        style={{ 
                          fontSize: '12px',
                          backgroundColor: '#ffc107', 
                          color: '#212529',
                          border: '1px solid #ffc107'
                        }}
                      >
                        Deactivate
                      </button>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteUser(user._id, user.name)}
                      className="btn"
                      disabled={processingUser === user._id}
                      style={{ 
                        fontSize: '12px',
                        backgroundColor: '#dc3545', 
                        color: 'white',
                        border: '1px solid #dc3545'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '24px' }}>
              Edit User Profile
            </h2>
            
            {editingUser && (
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <strong>Editing:</strong> {editingUser.name} ({editingUser.role})
                {editingUser.role === 'super-admin' && (
                  <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                    ⚠️ Super-admin profiles cannot be edited
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleUpdateUser}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  required
                  minLength="2"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter full name"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditFormChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Enter email address"
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Student ID
                </label>
                <input
                  type="text"
                  value={editingUser?.studentId || 'N/A'}
                  readOnly
                  disabled
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    cursor: 'not-allowed'
                  }}
                />
                <small style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
                  Student ID cannot be modified
                </small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="btn btn-outline"
                  disabled={processingUser === editingUser?._id}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={processingUser === editingUser?._id || !editForm.name.trim() || !editForm.email.trim()}
                >
                  {processingUser === editingUser?._id ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
